import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import i18n, { 
  supportedLanguages, 
  LanguageCode, 
  getLanguageFromPath, 
  getLocalizedPath,
  getPathWithoutLanguage,
  rtlLanguages
} from './config';

interface LanguageContextType {
  currentLanguage: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  getLocalizedHref: (path: string) => string;
  supportedLanguages: typeof supportedLanguages;
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>(() => 
    getLanguageFromPath(location.pathname)
  );

  const isRTL = rtlLanguages.includes(currentLanguage as typeof rtlLanguages[number]);

  // Update document direction for RTL languages
  useEffect(() => {
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage, isRTL]);

  useEffect(() => {
    const lang = getLanguageFromPath(location.pathname);
    if (lang !== currentLanguage) {
      setCurrentLanguage(lang);
      i18n.changeLanguage(lang);
    }
  }, [location.pathname]);

  const setLanguage = (lang: LanguageCode) => {
    if (lang === currentLanguage) return;
    
    const cleanPath = getPathWithoutLanguage(location.pathname);
    const newPath = getLocalizedPath(cleanPath, lang);
    
    setCurrentLanguage(lang);
    i18n.changeLanguage(lang);
    navigate(newPath);
  };

  const getLocalizedHref = (path: string): string => {
    return getLocalizedPath(path, currentLanguage);
  };

  return (
    <LanguageContext.Provider 
      value={{ 
        currentLanguage, 
        setLanguage, 
        getLocalizedHref,
        supportedLanguages,
        isRTL
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
